import styled from "styled-components";
import { getDeviceType, getTheme, logger, themed, FlexStream, FloatedImage, Flow, Stream, Text as Tx, Tooltip, } from "@bracket/ui";
import { formatDollars, formatWEI, } from "@formatters";

import type { Asset, } from "@bracket/types";
import type { ReactNode, } from "react";
import type { SummaryItemProps, } from "./SummaryItem";
import type { TwinSummaryItemProps, } from "./TwinSummaryItem";



export declare type MobileTwinSummaryItemProps = TwinSummaryItemProps & { twinSummary: boolean; }
export declare type MobileSingleSummaryItemProps = SummaryItemProps & { twinSummary: false; }
export declare type MobileSummaryItemProps = ( TwinSummaryItemProps | MobileSingleSummaryItemProps ) & { twinSummary: boolean; };



export const MobileSummaryItem = ( props: MobileSummaryItemProps ) => {
	themed();

	const { twinSummary, } = props;

	if(twinSummary) {

		const {
			label,
			value,
			value2,
			denomination,
			tip,
			showAsset = true,
			showDollars = true,
			showPercent = true,
			showPercentOnly = false,
			logo,
			x = false,
			x2 = false,
		} = props as MobileTwinSummaryItemProps;

		return (
			<MobSumItemBrace>
				{ logo && <FloatedImage url={logo} top="1.5rem" left=".5rem" width="25px" height="25px" style={{ transform: "translateY(-50%)", }} invertColors={getTheme().palette_type === "light"} /> }
				<MobSumLabel>
					<Tx md color="surface_variant_on" style={ logo ? { paddingLeft: "1.5rem", } : undefined } nowrap>
						{label}
						{tip && <Tooltip message={tip} /> }
					</Tx>
				</MobSumLabel>
				<MobSumValue>
					<Tx visible={showDollars && !showPercentOnly} justify="flex-end" accessor={CurrencyValue} xl nullOnFalsyAccessorValue>{value.usd}</Tx>
					{/* <Tx visible={showAsset && !showPercentOnly} justify="flex-end" accessor={WeiValue} accessorArgs={{ denomination, }} nullOnFalsyAccessorValue>{value.brkt}</Tx> */}
					<Tx visible={showPercent} justify="flex-end" accessor={PercentValue} accessorArgs={{ x, }} lg color="surface_variant_on" nullOnFalsyAccessorValue>{value.perc}</Tx>
					<Tx visible={showPercent} justify="flex-end" accessor={PercentValue} accessorArgs={{ x: x2, }} lg color="surface_variant_on" nullOnFalsyAccessorValue>{value2.perc}</Tx>
				</MobSumValue>
			</MobSumItemBrace>
		)
	}

	const {
		label,
		value,
		denomination,
		tip,
		showAsset = true,
		showDollars = true,
		showPercent = true,
		showPercentOnly = false,
		logo,
		x = false,
		noPercent = false,
	} = props;

	if(label === "Health Factor" && typeof value.perc === "number" && value.perc > 100) {
		return null;
	}


	return (
		<MobSumItemBrace>
			{ logo && <FloatedImage url={logo} top="1.5rem" left=".5rem" width="25px" height="25px" style={{ transform: "translateY(-50%)", }} invertColors={getTheme().palette_type === "light"} /> }
			<MobSumLabel>
				<Tx md color="surface_variant_on" style={ logo ? { paddingLeft: "1.5rem", } : undefined } nowrap>
					{label}
					{tip && <Tooltip message={tip} /> }
				</Tx>
			</MobSumLabel>
			<MobSumValue>
				<Tx visible={showDollars && !showPercentOnly} justify="flex-end" accessor={CurrencyValue} xl nullOnFalsyAccessorValue>{value.usd}</Tx>
				{/* <Tx visible={showAsset && !showPercentOnly} justify="flex-end" accessor={WeiValue} accessorArgs={{ denomination, }}>{value.brkt}</Tx> */}
				<Tx visible={showPercent && !noPercent} justify="flex-end" accessor={PercentValue} accessorArgs={{ x, }} lg color="surface_variant_on" nullOnFalsyAccessorValue>{value.perc}</Tx>
			</MobSumValue>
		</MobSumItemBrace>
	)

}

const CurrencyValue = ( value: unknown ): string => {
	try {
		const val = typeof value === "number" ? value : parseFloat(value as string);
		return formatDollars(val);
	}
	catch(error) {
		logger.warn(`@bracket/platform/SummaryItem:CurrencyValue - error formatting currency value of type ${typeof value} :: ${error instanceof Error ? error.message : error}`);
		return "N/A";
	}
}

const WeiValue = ( value: unknown, accessorArgs?: Record<string, unknown> ): ReactNode => {

	try {
		const { denomination, } = accessorArgs as { denomination: Asset; };
		if(!denomination) {
			throw new Error(`@bracket/platform/SummaryItem:WeiValue - invalid accessor arguments :: denomination is required`);
		}
		const { decimals, symbol, label, } = denomination;
		if(!denomination || typeof value !== "string") {
			throw new Error(`@bracket/platform/SummaryItem:WeiValue - invalid accessor arguments :: denomincation: ${typeof decimals}, symbol: ${typeof symbol}, value: ${typeof value}`);
		}
		return (
			<WeiFlow>
				<Tx md>{formatWEI(value, decimals, symbol === "USDC" ? 2 : 5)}</Tx>
				<Tx xsm color="outline" align="flex-start" style={{ marginTop: "-5px", }}>{label}</Tx>
			</WeiFlow>
		)
	}
	catch(error) {
		logger.warn(`@bracket/platform/SummaryItem:WeiValue - error formatting WEI value of type ${typeof value} :: ${error instanceof Error ? error.message : error}`);
		return <></>;
	}
}

const PercentValue = ( value: unknown, accessorArgs?: Record<string, unknown> ): string => {
	try {
		const { x, } = accessorArgs as { x: boolean; };
		const val = typeof value === "number" ? value : parseFloat(value as string);
		if(!isNaN(val)) {
			return `${x ? "" : val > 0 ? "+" : ""}${val.toFixed(2)}${x ? "x" : "%"}`;
		}
		throw new Error(`SummaryItem:PercentValue - invalid percent value of type ${typeof value} :: ${val}`);
	}
	catch(error) {
		logger.warn(`@bracket/platform/SummaryItem:PercentValue - error formatting percent value of type ${typeof value} :: ${error instanceof Error ? error.message : error}`);
		return "";
	}
}

const MobSumItemBrace = styled(Stream)<{ $logo?: string; }>`
	position: relative;
	width: 100%;
	background-color: ${() => getTheme().surface_container_lowest};
	border-radius: 8px;
	gap: 12px;
	padding: 15px;
	margin: 0.25rem;
	background-image: url(${props => props.$logo});
	height: 100%;
	// mobile exclusive below.
	@media (max-width: 1024px) {
		flex-direction: row;
		justify-content: space-between;
	}
`;



const MobSumLabel = styled(FlexStream)`
	color: ${() => getTheme().error_on};
`;


const MobSumValue = styled(Flow)`
	gap: .75rem;
`;


const WeiFlow = styled(Flow)`
`;
